<template>
  <div>
    <h5 class="text-center mb-2">Top 10 Countries</h5>
    <table class="table table-striped table-success table-sm table-bordered">
      <thead>
      <tr>
        <th>Country</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topCountries" :key="idx">
        <td>{{ item.country }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import countryMap from '@/utils/countryMapESEN.js';

const topCountries = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Asynchronous function that fetches visited link data from an API, processes country information,
 * and computes the top 10 countries based on the number of visits. The function updates relevant
 * reactive variables and handles API errors.
 *
 * @async
 * @function
 * @throws {Error} If the API request fails.
 *
 * @description
 * - Fetches data from the `/linksvisited` endpoint of the API.
 * - Translates country names using a predefined map (`countryMap`). If no translation exists, the original name is used.
 * - Counts the number of visits per country and sorts the results to extract the top 10.
 * - Updates reactive variables `loading`, `topCountries`, and `error` depending on the request state.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const res = await axios.get(`${apiUrl}/linksvisited`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        // Traduce el país si existe en el mapa, si no deja el original
        const countryES = item?.country || 'Unknown';
        const countryEN = countryMap[countryES] || countryES;
        counts[countryEN] = (counts[countryEN] || 0) + 1;
      });
      topCountries.value = Object.entries(counts)
          .map(([country, visits]) => ({ country, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load country data';
  } finally {
    loading.value = false;
  }
});
</script>
